import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { brl, fmtTimeBR, greeting } from "@/lib/format";
import { ArrowRight, Calendar, TrendingUp, Users, Scissors, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const cachedDashboard = (() => {
    try {
      const raw = sessionStorage.getItem("nailcontrol.dashboard");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  const [data, setData] = useState(cachedDashboard);
  const [loading, setLoading] = useState(!cachedDashboard);

  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const demoMode = params.get("demo");

        if (demoMode === "1") {
          const seeded = await api.post("/demo/seed");
          toast.success(seeded.data?.message || "Dados fictícios adicionados!");
          window.history.replaceState({}, "", window.location.pathname);
        } else if (demoMode === "clear") {
          const cleared = await api.delete("/demo/seed");
          toast.success(cleared.data?.message || "Dados fictícios removidos!");
          window.history.replaceState({}, "", window.location.pathname);
        }

        const r = await api.get("/dashboard");
        setData(r.data);
        try { sessionStorage.setItem("nailcontrol.dashboard", JSON.stringify(r.data)); } catch {}
      } catch (e) {
        toast.error(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div data-testid="dash-loading" className="text-[#71717A]">Carregando…</div>;
  if (!data) return null;

  const name = user?.display_name || user?.name || "";

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-[#27272A]">{greeting()}, {name}.</h1>
        <p className="text-[#71717A] text-sm mt-1">Veja como está seu negócio hoje.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <MetricCard label="Faturamento do mês" value={brl(data.faturamento_mes)} icon={TrendingUp} tone="burgundy" tid="metric-faturamento" />
        <MetricCard label="Atendimentos" value={data.atendimentos_mes} icon={Calendar} tid="metric-atendimentos" />
        <MetricCard label="Ticket médio" value={brl(data.ticket_medio)} icon={Users} tid="metric-ticket" />
        <MetricCard label="Resultado do mês" value={brl(data.resultado_mes)} icon={TrendingUp} tone={data.resultado_mes >= 0 ? "positive" : "warning"} tid="metric-resultado" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="lg:col-span-2 card-nc p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg text-[#27272A]">Agenda de hoje</h2>
            <Link to="/app/agenda" className="text-xs text-[#682F3E] font-semibold flex items-center gap-1" data-testid="link-agenda">
              Ver agenda <ArrowRight size={14} />
            </Link>
          </div>
          {data.today_appointments.length === 0 ? (
            <div className="text-center py-8 text-[#71717A] text-sm">
              Você ainda não tem atendimentos hoje.
            </div>
          ) : (
            <div className="space-y-2">
              {data.today_appointments.map(a => (
                <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl bg-[#FAF8F6] border border-[#E4E4E7]/50" data-testid={`today-appt-${a.id}`}>
                  <div className="font-mono-brand text-sm font-bold text-[#682F3E] w-14">{fmtTimeBR(a.time)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#27272A] truncate">{a.client_name}</div>
                    <div className="text-xs text-[#71717A]">{a.service_name}</div>
                  </div>
                  <div className="text-sm font-mono-brand font-semibold text-[#27272A]">{brl(a.price)}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="card-nc p-5">
          <h2 className="font-display font-bold text-lg text-[#27272A] mb-3">Financeiro</h2>
          <div className="space-y-3">
            <FinRow label="Receitas" value={brl(data.receitas_mes)} tone="positive" />
            <FinRow label="Despesas" value={brl(data.despesas_mes)} tone="warning" />
            <div className="divider-soft" />
            <FinRow label="Resultado" value={brl(data.resultado_mes)} strong tone={data.resultado_mes >= 0 ? "positive" : "warning"} />
          </div>
        </section>
      </div>

      <section className="card-nc p-5">
        <h2 className="font-display font-bold text-lg text-[#27272A] mb-3">Últimos 6 meses</h2>
        <div className="h-56">
          <ResponsiveContainer>
            <LineChart data={data.trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#F5ECE8" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => brl(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E4E4E7" }} />
              <Line type="monotone" dataKey="receitas" stroke="#682F3E" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="despesas" stroke="#B76E56" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="4 4" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      {data.pricing_alerts.length > 0 && (
        <section className="card-nc p-5 border-l-4 border-l-[#B76E56]" data-testid="pricing-alerts">
          <div className="flex items-start gap-3 mb-3">
            <Info size={18} className="text-[#B76E56] mt-0.5" />
            <div>
              <h2 className="font-display font-bold text-lg text-[#27272A]">Seus preços</h2>
              <p className="text-sm text-[#71717A]">
                {data.pricing_alerts.length} {data.pricing_alerts.length === 1 ? "serviço merece" : "serviços merecem"} sua atenção.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            {data.pricing_alerts.map(p => (
              <div key={p.id} className="p-3 rounded-xl bg-[#F9ECE8]/50 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-[#27272A]">{p.name}</div>
                  <div className="text-xs text-[#71717A]">Preço atual: {brl(p.current_price)} · Cenário calculado: {brl(p.calculated_price)}</div>
                </div>
              </div>
            ))}
          </div>
          <Link to="/app/precificacao" className="btn-primary-nc inline-flex mt-4 text-sm" data-testid="btn-revise-pricing">Revisar precificação</Link>
        </section>
      )}
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, tone, tid }) {
  const toneClass = tone === "burgundy" ? "text-[#682F3E]" : tone === "positive" ? "text-[#4F7A61]" : tone === "warning" ? "text-[#B76E56]" : "text-[#27272A]";
  return (
    <div className="card-nc p-4" data-testid={tid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-[#71717A]">{label}</span>
        <Icon size={16} className="text-[#C98F96]" />
      </div>
      <div className={`text-xl md:text-2xl font-mono-brand font-bold ${toneClass}`}>{value}</div>
    </div>
  );
}
function FinRow({ label, value, tone, strong }) {
  const toneClass = tone === "positive" ? "text-[#4F7A61]" : tone === "warning" ? "text-[#B76E56]" : "text-[#27272A]";
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${strong ? "font-bold text-[#27272A]" : "text-[#71717A]"}`}>{label}</span>
      <span className={`font-mono-brand ${strong ? "text-lg font-bold" : "text-sm font-semibold"} ${toneClass}`}>{value}</span>
    </div>
  );
}
